/**
 * Splash - minimal AI-themed boot screen. Logo + soft pulsing aura. Auto-dismisses
 * after a short delay (or instantly once any v4 page paints, whichever first).
 * No language picker - default is English, with FA togglable in Settings.
 */

import { useEffect, useState } from 'react';
import { useV4 } from './i18n';
import { AnimatedRoniDocLogo } from './AnimatedRoniDocLogo';

const SHOWN_KEY = 'client-splash-shown';
const SPLASH_DURATION_MS = 1200;

export default function SplashV4() {
  const { t, accent } = useV4();
  const [visible, setVisible] = useState(() => {
    try { return sessionStorage.getItem(SHOWN_KEY) !== '1'; } catch { return true; }
  });

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(dismiss, SPLASH_DURATION_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    try { sessionStorage.setItem(SHOWN_KEY, '1'); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      onClick={dismiss}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background animate-in fade-in duration-300 cursor-pointer"
    >
      {/* Pulsing aura behind the logo */}
      <div className="relative flex items-center justify-center" style={{ width: 160, height: 160 }}>
        <span
          aria-hidden
          className="absolute inset-0 rounded-full opacity-30 animate-ping"
          style={{ background: `radial-gradient(circle, ${accent.primary}55 0%, transparent 70%)`, animationDuration: '2.2s' }}
        />
        <span
          aria-hidden
          className="absolute inset-6 rounded-full opacity-50 animate-pulse"
          style={{ background: `radial-gradient(circle, ${accent.primary}33 0%, transparent 70%)`, animationDuration: '1.6s' }}
        />
        <img
          src="/client/icon.png"
          alt="RoniDoc"
          className="relative h-24 w-24 rounded-2xl shadow-lg"
        />
      </div>

      <p className="mt-8 text-sm font-semibold text-foreground/80">{t('app_name')}</p>

      {/* RoniDoc.ai mark - dots above the R ease in each time splash mounts */}
      <div className="mt-6">
        <AnimatedRoniDocLogo style={{ fontSize: '2.25rem' }} />
      </div>

      {/* Thinking dots */}
      <div className="mt-4 flex items-center gap-1.5" aria-label="Loading">
        {[0, 1, 2].map(i => (
          <span
            key={i}
            className="block h-2 w-2 rounded-full"
            style={{
              background: accent.primary,
              animation: 'splashDot 1.1s ease-in-out infinite',
              animationDelay: `${i * 0.15}s`,
              opacity: 0.4,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes splashDot {
          0%, 80%, 100% { transform: scale(0.85); opacity: 0.4; }
          40% { transform: scale(1.25); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
